import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Phone, ChevronLeft, ChevronDown,
  Award, Clock, ShieldCheck, CheckCircle2, ArrowUpRight,
} from "lucide-react";
import Seo from "@/components/Seo";
import TrustBadges from "@/components/TrustBadges";
import { FadeUp, StaggerContainer, StaggerItem } from "@/components/ui/motion";
import { SERVICE_CATEGORIES } from "@/data/services";

const ease = [0.22, 1, 0.36, 1];
const WHY_ICONS = [Award, Clock, ShieldCheck];

const FaqItem = ({ q, a, open, onToggle }) => (
  <div className="border-b border-slate-200 last:border-0">
    <button
      className="w-full flex items-center justify-between py-5 text-left gap-4"
      onClick={onToggle}
    >
      <span className="font-heading font-semibold text-slate-900">{q}</span>
      <motion.span
        animate={{ rotate: open ? 180 : 0 }}
        transition={{ duration: 0.25, ease }}
        className="shrink-0 text-slate-400"
      >
        <ChevronDown className="h-5 w-5" />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="body"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease }}
          style={{ overflow: "hidden" }}
        >
          <p className="pb-5 text-sm text-slate-600 leading-relaxed">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const cat = SERVICE_CATEGORIES.find((c) => c.slug === slug);
  const [openFaq, setOpenFaq] = useState(null);

  if (!cat) return <Navigate to="/services" replace />;

  const Icon = cat.icon;
  const items = cat.items[lang] || cat.items.fr;
  const others = SERVICE_CATEGORIES.filter((c) => c.slug !== slug).slice(0, 4);
  const whyItems = t("service.why_items", { returnObjects: true });
  const steps = t("service.steps", { returnObjects: true });
  const faqs = t("service.faqs", { returnObjects: true });
  const stats = t("service.stats", { returnObjects: true });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("nav.home"), item: "https://bionet-solutions.com" },
      { "@type": "ListItem", position: 2, name: t("nav.services"), item: "https://bionet-solutions.com/services" },
      { "@type": "ListItem", position: 3, name: t(`categories.${slug}.name`) },
    ],
  };

  return (
    <>
      <Seo
        titleKey="seo.services_title"
        descKey="seo.services_desc"
        path={`/services/${slug}`}
        schema={breadcrumbSchema}
      />

      {/* ── 1. HERO ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40" />
          <div className="absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-emerald-200/40 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-[24rem] w-[24rem] rounded-full bg-sky-200/30 blur-3xl" />
        </div>
        <div className="container-x pt-10 pb-16 lg:pt-16 lg:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-green mb-6 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" /> {t("nav.services")}
            </Link>
            <div className="pill mb-5 w-fit">
              <Icon className="h-3.5 w-3.5 text-brand-green" />
              <span>{t("service.hero_badge")}</span>
            </div>
            <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-[4rem] text-slate-900 leading-[1.02]">
              {t(`categories.${cat.slug}.name`)}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              {t(`categories.${cat.slug}.tagline`)}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/devis" className="btn-primary group" data-testid="service-cta-quote">
                {t("common.cta_quote")}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a href="tel:+33649502801" className="btn-secondary" data-testid="service-cta-call">
                <Phone className="h-4 w-4" /> 06 49 50 28 01
              </a>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease }}
          >
            <div className="relative">
              <div className={`absolute -inset-3 rounded-[32px] bg-gradient-to-br ${cat.accent} blur-2xl opacity-70`} />
              <div className={`relative rounded-3xl p-8 bg-gradient-to-br ${cat.accent} border border-slate-200/60 ring-soft`}>
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/80 shadow-sm">
                  <Icon className="h-8 w-8 text-brand-green" />
                </div>
                <div className="mt-6 space-y-2.5">
                  {items.slice(0, 5).map((item) => (
                    <div key={item} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-brand-green shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                  {items.length > 5 && (
                    <p className="text-xs text-slate-500 pl-6">
                      + {items.length - 5} {t("service.more_items")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container-x py-6">
        <TrustBadges />
      </div>

      {/* ── 2. STATS BAR ───────────────────────────────────────────── */}
      <div className="border-y border-slate-200 bg-white py-10">
        <StaggerContainer className="container-x grid grid-cols-3 divide-x divide-slate-200">
          {Array.isArray(stats) && stats.map((s) => (
            <StaggerItem key={s.value} className="text-center px-2 sm:px-8">
              <div className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl text-slate-900">{s.value}</div>
              <div className="text-xs sm:text-sm text-slate-500 mt-1">{s.label}</div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>

      {/* ── 3. WHY CHOOSE US ───────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-x">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-12 max-w-xl">
              {t("service.why_title")}
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {Array.isArray(whyItems) && whyItems.map((item, i) => {
              const WIcon = WHY_ICONS[i] || ShieldCheck;
              return (
                <StaggerItem key={i}>
                  <div className="rounded-3xl border border-slate-200 bg-white p-7 ring-soft h-full">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
                      <WIcon className="h-6 w-6 text-brand-green" />
                    </div>
                    <h3 className="mt-5 font-heading font-bold text-lg text-slate-900">{item.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 4. PROCESS ─────────────────────────────────────────────── */}
      <section className="section bg-slate-50">
        <div className="container-x">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-12">
              {t("service.process_title")}
            </h2>
          </FadeUp>
          <div className="relative">
            <div className="hidden lg:block absolute top-5 left-[12%] right-[12%] h-px bg-slate-300" />
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-4">
              {Array.isArray(steps) && steps.map((step, i) => (
                <FadeUp key={i} delay={i * 0.1}>
                  <motion.div
                    className="h-10 w-10 rounded-full bg-brand-green text-white flex items-center justify-center font-heading font-bold text-sm mb-4 relative z-10"
                    initial={{ scale: 0.6, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, ease, delay: i * 0.1 }}
                  >
                    {i + 1}
                  </motion.div>
                  <h4 className="font-heading font-bold text-slate-900 mb-2">{step.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. PRESTATIONS GRID ─────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-x">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-10">
              {t("service.features_title")}
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8">
            {items.map((it) => (
              <StaggerItem key={it}>
                <div className="flex items-start gap-3 py-3 border-b border-slate-100">
                  <CheckCircle2 className="h-4 w-4 text-brand-green mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700">{it}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ── 6. FAQ ──────────────────────────────────────────────────── */}
      <section className="section bg-slate-50">
        <div className="container-x max-w-3xl">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl text-slate-900 mb-8">
              {t("service.faq_title")}
            </h2>
          </FadeUp>
          <div className="rounded-3xl bg-white border border-slate-200 ring-soft px-6 sm:px-8">
            {Array.isArray(faqs) && faqs.map((faq, i) => (
              <FaqItem
                key={i}
                q={faq.q}
                a={faq.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. OTHER SERVICES ───────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-x">
          <FadeUp>
            <h3 className="font-heading font-bold text-2xl text-slate-900 mb-8">
              {t("service.other_services")}
            </h3>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {others.map((o) => {
              const I = o.icon;
              return (
                <motion.div
                  key={o.slug}
                  whileHover={{ y: -4, transition: { type: "spring", stiffness: 300, damping: 20 } }}
                >
                  <Link
                    to={`/services/${o.slug}`}
                    className="group block h-full rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-blue/40 hover:shadow-md transition-shadow ring-soft"
                    data-testid={`related-service-${o.slug}`}
                  >
                    <I className="h-5 w-5 text-brand-green" />
                    <div className="mt-3 font-heading font-semibold text-slate-900">
                      {t(`categories.${o.slug}.name`)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                      {t(`categories.${o.slug}.tagline`)}
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-green">
                      {t("services_overview.cta_more")}
                      <ArrowUpRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 8. FINAL CTA ────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-br from-[#166534] via-emerald-700 to-sky-800">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-sky-300/20 blur-3xl" />
        </div>
        <div className="container-x relative text-center">
          <FadeUp>
            <h2 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-white">
              {t("service.cta_title")}
            </h2>
            <p className="mt-4 text-emerald-100 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              {t("service.cta_subtitle")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/devis"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#166534] font-semibold px-7 py-3.5 text-sm hover:bg-slate-50 transition-colors group"
              >
                {t("common.cta_quote")}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+33649502801"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white font-semibold px-7 py-3.5 text-sm hover:bg-white/10 transition-colors"
              >
                <Phone className="h-4 w-4" /> 06 49 50 28 01
              </a>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default ServiceDetailPage;
